import type {
  CurrentPageContext,
  FrozenActionBatch,
  PlatformAction,
  PlatformActionBatchResult,
} from "@playbookai/types";
import type { PlayerAIFailure } from "./failures.js";

export type {
  CurrentPageContext,
  PlatformEntityRef,
  PlatformNavigationAction,
  PrivatePlayerAIHandoff,
} from "@playbookai/types";

export type ConnectionState =
  | "idle"
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed"
  | "error";

export type ConversationType = "private" | "workspace";
export type WorkspaceType = "solve" | "hoshin" | "hoshin_workshop" | "vsm";
/**
 * Workspace types that host owner-private contextual threads: a Hoshin plan
 * (Edit/Monitor advice), a Hoshin workshop (Ideate) and a VSM (each
 * participant's own preparation thread). SOLVE has one shared workspace
 * conversation and nothing else; the server's path enum is the authority and
 * rejects it before authorization runs.
 */
export type ContextualPrivateWorkspaceType =
  | "hoshin"
  | "hoshin_workshop"
  | "vsm";

/**
 * Workspace types with exactly ONE private conversation per participant.
 *
 * A VSM participant keeps one durable preparation thread so their context
 * survives phase moves; a Hoshin plan deliberately allows several. These types
 * resolve their conversation instead of listing and creating one, which is also
 * what makes two tabs opening at once converge rather than fork.
 */
export const SINGLETON_PRIVATE_WORKSPACE_TYPES = ["vsm"] as const;
export type SingletonPrivateWorkspaceType =
  (typeof SINGLETON_PRIVATE_WORKSPACE_TYPES)[number];

export function hasSingletonPrivateConversation(
  workspaceType: string,
): workspaceType is SingletonPrivateWorkspaceType {
  return (SINGLETON_PRIVATE_WORKSPACE_TYPES as readonly string[]).includes(
    workspaceType,
  );
}

/**
 * The V2 API's own ceiling for one message's text, mirrored from
 * `CreateSharedMessageRequest.text` / `MessageTrigger.text` in
 * `services/player-ai/player_ai/v2/schemas.py`.
 *
 * Mirrored so a composer can refuse an oversized message and say why, instead
 * of discovering the limit as a 422 after the user has already lost the send.
 * The server stays authoritative; this never trims anything.
 */
export const MAX_MESSAGE_CHARACTERS = 32_000;

/**
 * The V2 API's own ceiling for how many files one message may carry, mirrored
 * from `MAX_MESSAGE_ATTACHMENTS` in `services/player-ai/player_ai/v2/schemas.py`
 * (`maxItems` on `attachment_ids` in both message bodies).
 *
 * Mirrored so a composer can stop at the limit before any request, instead of
 * a send failing with a 422. The server stays authoritative; a test pins both
 * mirrored numbers to `docs/api/player-ai.yaml`.
 */
export const MAX_MESSAGE_ATTACHMENTS = 10;

/** Which conversation surface the SERVER routed this actor to. */
export type ConversationSurface = "private" | "group";

/**
 * The server's routing answer for one workspace.
 *
 * `conversationMode` is reported by the server rather than inferred from the
 * route, so the rail can label the surface truthfully: the private/group
 * boundary is a product state change, and a client that guessed it could label
 * a shared thread "Only you".
 */
export interface ActiveConversationResult {
  conversation: Conversation;
  capabilities: Capabilities;
  conversation_mode: ConversationSurface;
  execution_status?: string | null;
  execution_revision?: string | null;
  is_effective_driver?: boolean;
}

export type RunStatus =
  | "queued"
  | "running"
  | "waiting_documents"
  | "waiting_approval"
  | "completed"
  | "failed";
export type RunOutcome =
  | "final_response"
  | "needs_input"
  | "proposal_expired"
  | "limit_reached"
  | "runtime_failure"
  /** Someone stopped the Run; what it had shown is kept, marked stopped. */
  | "stopped";
export type AssistantMessageDelivery = "stream" | "buffered";
export type HoshinSurfaceActionId =
  | "hoshin.review_duplicate_ideas"
  | "hoshin.group_similar_ideas"
  | "hoshin.suggest_impact_effort";

export interface DuplicateIdeaSection {
  relationship: "exact_duplicate" | "near_duplicate";
  idea_ids: string[];
  suggested_idea: string;
}

export interface DuplicateIdeaResult {
  sections: DuplicateIdeaSection[];
}

export interface GroupingPreviewTheme {
  key: string;
  name: string;
  description: string;
  idea_ids: string[];
}

export interface GroupingPreviewResult {
  themes: GroupingPreviewTheme[];
  ungrouped_idea_ids: string[];
}

export interface ImpactEffortPreviewPlacement {
  group_id: string;
  impact: number;
  effort: number;
}

export interface ImpactEffortPreviewResult {
  placements: ImpactEffortPreviewPlacement[];
}

export type HoshinSurfaceActionPreview =
  | {
      action_id: "hoshin.review_duplicate_ideas";
      source_revision: string;
      result: DuplicateIdeaResult;
    }
  | {
      action_id: "hoshin.group_similar_ideas";
      source_revision: string;
      result: GroupingPreviewResult;
    }
  | {
      action_id: "hoshin.suggest_impact_effort";
      source_revision: string;
      result: ImpactEffortPreviewResult;
    };

export type ReasoningEffort =
  | "none"
  | "low"
  | "medium"
  | "high"
  | "xhigh"
  | "max";
export type ModelPolicyConnectionKey =
  | "reasoning"
  | "fast"
  | "reasoning-sol"
  | "reasoning-opus"
  | "reasoning-fable"
  | "reasoning-astra";
export type ModelPolicyId =
  | `${Exclude<ModelPolicyConnectionKey, "reasoning-opus" | "reasoning-fable">}-${ReasoningEffort}`
  | `${"reasoning-opus" | "reasoning-fable"}-${Exclude<ReasoningEffort, "max">}`;
export type ProposalStatus =
  | "pending"
  | "applying"
  | "applied"
  | "failed"
  | "rejected"
  | "expired"
  | "stale";
export type AttachmentStatus =
  | "creating"
  | "uploading"
  | "checking"
  | "ready"
  | "blocked"
  | "failed"
  | "unlinked"
  | "invalidated"
  | "deleted";
export type DocumentRetrievalStatus = "processing" | "ready" | "failed";

export interface ScopeRef {
  module_id: string;
  surface_type: string;
  surface_id: string;
}

export type EmptyScopeRef = Record<string, never>;

export interface TargetRef {
  type: "widget";
  scope_ref: ScopeRef;
  widget_id: string;
  widget_kind: string;
}

export interface WorkspaceTargetRef {
  type: "workspace";
  workspace_type: WorkspaceType;
  workspace_id: string;
}

/**
 * The target of a governed EXTERNAL write.
 *
 * Carries the platform's own execution id and nothing else — no provider
 * object id, no connection id, no site. What is being written lives in the
 * proposal's bounded preview; this only says which prepared execution the
 * approval binds to.
 *
 * The runtime has always written this shape for integration proposals; this
 * union arm was missing, so `target_ref` claimed to be a widget or a workspace
 * for every one of them.
 */
export interface IntegrationExecutionTargetRef {
  type: "integration_execution";
  execution_id: string;
}

export interface PrivateIdeateCanvasContext {
  workshop_id: string;
  participant_user_id: string;
  captured_at: string;
  notes: { note_id: string; text: string }[];
  truncated: boolean;
}

export interface InteractionContext {
  active_surface_ref?: ScopeRef;
  selection_refs?: TargetRef[];
  /** Ephemeral shell context; owner-service authorization remains authoritative. */
  current_page_context?: CurrentPageContext;
  private_ideate_canvas?: PrivateIdeateCanvasContext;
  current_page_captured_at?: string;
}

export interface BrowserCurrentDate {
  date: string;
  time_zone: string;
  source: "browser";
}

export type PlayerAIScope =
  | { conversationType: "private" }
  | {
      conversationType: "private";
      workspaceType: ContextualPrivateWorkspaceType;
      workspaceId: string;
    }
  | {
      conversationType: "workspace";
      workspaceType: WorkspaceType;
      workspaceId: string;
    };

export interface Capabilities {
  can_read: boolean;
  can_post_message: boolean;
  can_start_run: boolean;
  can_decide_proposal: boolean;
  /**
   * SOLVE group collaboration facts (ADR-0044), already gated server-side by
   * the Player AI rollout flag. Absent from servers that predate the contract;
   * treat a missing block as individual mode.
   */
  collaboration?: GroupCollaborationCapabilities;
  /**
   * SOLVE's shared conversation tone, and whether THIS caller may change it
   * (`change_settings` — Owner or active Facilitator). Present only on a SOLVE
   * workspace conversation; every participant reads the style, so a missing
   * `can_change_response_style` means read-only, never hidden.
   */
  solve_response_style?: SolveResponseStyle | null;
  can_change_response_style?: boolean;
  /** Shared SOLVE-level permission; absent outside SOLVE workspaces. */
  web_search_enabled?: boolean | null;
  solve_web_search_enabled?: boolean | null;
  can_change_web_search?: boolean;
  /**
   * Whether THIS caller may Stop a Run and, in a group, Resume or Discard
   * pending requests: whoever may start Runs in individual mode; the
   * workspace facilitator or owner in a group. Absent means no.
   */
  can_control_runs?: boolean;
}

/** Stable wire values behind the labels Direct and Empathetic. */
export type SolveResponseStyle = "direct" | "empathetic";

export interface SolveResponseStyleState {
  response_style: SolveResponseStyle;
  can_change: boolean;
}

export interface SolveWebSearchState {
  enabled: boolean;
  can_change: boolean;
}

export type CollaborationMode = "individual" | "group";

export interface GroupCollaborationCapabilities {
  mode: CollaborationMode;
  effective_ai_contributor_count: number;
  has_active_facilitator: boolean;
  can_mention_player_ai: boolean;
  /** Exclusive: the active Facilitator, or the Owner when there is none. */
  can_configure_group_ai: boolean;
  can_decide_group_proposal: boolean;
  decisions_restricted_to_driver?: boolean;
}

/** Off, or a finish-relative review every 1 to 15 whole minutes. */
export interface GroupSchedule {
  enabled: boolean;
  interval_minutes: number;
}

export interface GroupScheduleState extends GroupSchedule {
  /** `enabled` is stored intent; `effective` also requires active group mode. */
  effective: boolean;
  next_check_at: string | null;
}

export interface GroupLastReview {
  finished_at: string;
  disposition: "respond" | "no_response" | "skipped";
}

export interface GroupSettings {
  mode: CollaborationMode;
  schedule: GroupScheduleState;
  can_configure: boolean;
  pending_mention_count: number;
  last_review?: GroupLastReview | null;
  /** The shared Stop state, or null/absent while Player AI runs normally. */
  pause?: GroupPause | null;
}

/**
 * A group's shared Stop state. `stopping` while the stopped Run winds down;
 * `awaiting_decision` while requests collected meanwhile wait for Resume or
 * Discard pending. Every participant sees the same state and count.
 */
export interface GroupPause {
  state: "stopping" | "awaiting_decision";
  /** Named back by Resume and Discard so a stale click cannot act on a later pause. */
  paused_run_id: string | null;
  paused_at: string;
  /**
   * Requests people sent while paused. Owed phase-entry guidance is held
   * through the pause but is not a request, so it is never counted here.
   */
  pending_request_count: number;
  /** The newest waiting request's sequence — Discard pending's cutoff. */
  through_message_sequence: number;
}

/** Messages in a conversation that mention the viewer (a total, not unread). */
export interface MentionIndex {
  count: number;
  /** Ascending; the most recent 1000 when there are more. */
  message_sequences?: number[];
}

/** One person typing in a group conversation. Never draft text. */
export interface TypingParticipant {
  user_id: string;
  display_name: string;
  expires_at: string;
}

export interface TypingEnvelope {
  typing?: TypingParticipant[];
}

export interface GroupSettingsEnvelope {
  group_settings: GroupSettings;
}

export interface UpdateGroupSettingsInput {
  client_action_id: string;
  schedule: GroupSchedule;
}

/** A validated structured mention recorded on a workspace message. */
export interface MessageMention {
  /** `user` names a person (Task 6): it addresses them and notifies nobody. */
  type: "player_ai" | "user";
  /** Zero-based ordinals of the mention's token selected by the mention UI. */
  occurrence_indices?: number[];
  /** The mentioned person; `user` only. */
  user_id?: string | null;
  /** The exact `@name` the picker inserted; `user` only. Display only. */
  token?: string | null;
}

/** What a reply persists: the Player AI message it answers, never its text. */
export interface MessageReplyReference {
  message_id: string;
  message_sequence: number;
}

/**
 * The message(s) that started the Run behind a Player AI response (Task 7),
 * stamped on the response when it is written. Absent when no message started
 * the Run — an opening, a phase entry, a scheduled review.
 */
export interface MessageTriggerPreview {
  message_id: string;
  message_sequence: number;
  /** Bounded; the UI truncates it to one line. */
  text: string;
  author_display_name?: string | null;
  /** How many more triggering messages the same Run handled ("+N"). */
  additional_count: number;
  /** Everyone whose message the Run handled (bounded). */
  author_user_ids?: string[];
}

export interface MessageCommand {
  type: "next_phase";
}

export interface AssistantSnapshot {
  run_id: string;
  event_sequence: number;
  attempt_epoch: number;
  delta_sequence: number;
  text: string;
}

export interface RunProgress {
  stage: "reviewing_canvas" | "preparing_canvas_proposal";
  started_at: string;
}

export type GroundingSourceType =
  | "conversation_attachment"
  | "vsm_preparation_source";

/**
 * A stable owner-link handle for a source a Run used. Never a document ID,
 * blob key, or filename, and never an authorization credential — the host
 * resolves a display name only from metadata it is already authorized to see.
 */
export interface GroundingSourceRef {
  source_type: GroundingSourceType;
  source_id: string;
}

/** The kinds this client version has a name for. */
export type KnownRunActivityKind =
  | "compacting_context"
  | "checking_documents"
  | "searching_documents"
  | "reading_document"
  | "reading_document_section"
  | "reviewing_canvas"
  | "preparing_answer"
  | "preparing_canvas_proposal"
  | "applying_approved_changes"
  | "reading_integration"
  | "preparing_integration_change"
  | "applying_integration_change"
  | "loading_guidance"
  | "reading_canvas_context"
  | "checking_canvas_widgets"
  | "inspecting_widgets"
  | "checking_widget_actions"
  | "reading_widget"
  | "requesting_canvas_review"
  | "preparing_phase_advance"
  | "checking_workshop_documents"
  | "checking_comment_threads"
  | "checking_participants"
  | "checking_map_layout"
  | "reading_workshop_document"
  | "reading_comments"
  | "reading_map"
  | "reading_improvements"
  | "reading_vsm_workspace"
  | "inspecting_map_item"
  | "inspecting_map_items"
  | "inspecting_map_neighbourhood"
  | "reviewing_preparation"
  | "preparing_framing"
  | "preparing_sipoc"
  | "preparing_comment"
  | "preparing_map"
  | "preparing_improvements"
  | "preparing_impact_effort"
  | "preparing_implementation"
  | "preparing_control_plan"
  | "preparing_workshop_notes"
  | "preparing_file_sharing"
  | "reading_linked_solve"
  | "reading_workshop_ideas"
  | "preparing_private_ideas"
  | "preparing_idea_submission"
  | "preparing_duplicate_review"
  | "preparing_idea_groups"
  | "preparing_idea_scores"
  | "preparing_x_matrix"
  | "reading_x_matrix"
  | "getting_work_overview"
  | "checking_work_items"
  | "reading_work_item"
  | "checking_work_changes"
  | "searching_workspace"
  | "preparing_navigation"
  | "searching_kpis"
  | "checking_notifications"
  | "reading_notification"
  | "checking_unread_notifications"
  | "reading_settings"
  | "reading_mood"
  | "reading_questionnaire"
  | "reading_campaign"
  | "reading_assignment"
  | "reading_assessment_report"
  | "reading_assessment_responses"
  | "checking_campaign_progress"
  | "checking_assessment_reports"
  | "searching_admin"
  | "reading_admin"
  | "comparing_admin"
  | "reading_audit"
  | "preparing_settings"
  | "preparing_notification_preferences"
  | "preparing_acknowledgement"
  | "preparing_resource_sharing"
  | "preparing_improvement_owner"
  | "preparing_improvement_date"
  | "preparing_improvement_status"
  | "preparing_objective_owner"
  | "preparing_target_date"
  | "preparing_hoshin_status"
  | "preparing_assessment_comment"
  | "preparing_assessment_responses"
  | "preparing_assessment_submission"
  | "preparing_notification_read"
  | "preparing_notification_unread"
  | "preparing_notifications_read"
  | "preparing_resource_unsharing"
  | "preparing_widget_create"
  | "preparing_widget_change"
  | "preparing_widget_add"
  | "preparing_widget_remove"
  | "preparing_widget_unplace"
  | "applying_tool_change"
  | "checking_proposal_status"
  | "applying_widget_change"
  | "running_tool";

/**
 * What a `run.activity` frame may actually carry.
 *
 * OPEN by construction. A browser tab loaded before a deploy keeps its old
 * bundle while the service behind it starts emitting an additive kind, so
 * narrowing this to the known union made the client DROP those frames — the
 * activity panel then stalled on a row that never completed. `(string & {})`
 * keeps editor completion for the known members while accepting the rest.
 */
export type RunActivityKind = KnownRunActivityKind | (string & {});

export type RunActivityStatus = "started" | "completed" | "failed";

/** Bound on a transported activity kind. Mirrors the service's own pattern. */
export const MAX_RUN_ACTIVITY_KIND_LENGTH = 64;

/**
 * Which connected service an integration activity touched.
 *
 * CLOSED, unlike `RunActivityKind`, and the difference is deliberate. A kind
 * selects a SENTENCE, so an unknown one still needs a row and degrades to
 * generic copy; a service selects a NAME, and the only safe thing to do with a
 * name this bundle cannot vouch for is not print it. The value originates in
 * connector and platform code, never in remote catalogue metadata or a tool
 * name, so no untrusted party can choose the words a user reads.
 *
 * An unrecognised value therefore narrows to `null` at the parser — the frame
 * is kept, the provider name is not.
 */
export type RunActivityServiceKind = "jira" | "confluence";

/** Bound on `RunActivity.safe_count`. Mirrors the service's own ceiling. */
export const MAX_RUN_ACTIVITY_SAFE_COUNT = 999;

/** Bound on `RunActivity.safe_label`. Mirrors the service's own ceiling. */
export const MAX_RUN_ACTIVITY_SAFE_LABEL_LENGTH = 120;

/** One replayable, content-free unit of visible Run work. */
export interface RunActivity {
  activity_id: string;
  attempt_epoch: number;
  kind: RunActivityKind;
  status: RunActivityStatus;
  source_refs: GroundingSourceRef[];
  failure_code: string | null;
  /**
   * The trusted service classification, or `null` when nobody declared one or
   * this bundle does not recognise the one that arrived.
   */
  service_kind: RunActivityServiceKind | null;
  /**
   * How many equivalent operations this one folded row stands for. `null` and
   * `1` both mean a single operation.
   */
  safe_count: number | null;
  /** Code-owned widget type label. UI renders only its closed vocabulary. */
  safe_label: string | null;
  started_at: string;
  updated_at: string;
}

/**
 * Safe durable projection of the latest terminal Run failure.
 *
 * The three additive fields exist so the diagnostics a user copies are the SAME
 * set live and after a reload. Live, the `run.failed` frame carries the whole
 * Run and the client reads them from it; after a reload the failed Run is not
 * the ACTIVE Run, so `active_run` is null and they would otherwise be missing
 * from exactly the case a support reader is most likely to be handed. All three
 * are durable columns already on the Run row and are read under the same
 * tenant-scoped query — no new persistence, no new telemetry.
 *
 * Optional because a server that predates them omits them; a missing field is
 * dropped from the copied block rather than invented.
 */
export interface RunFailure {
  run_id: string;
  error_code: string;
  occurred_at: string;
  /** Retry generation of the failed Run. */
  attempt_epoch?: number;
  /** The Run's terminal outcome, when it recorded one. */
  outcome?: RunOutcome | null;
  /** The runtime release that executed the failed Run. */
  runtime_release?: string;
  /**
   * What started the failed Run, and the Message it was answering.
   *
   * A failed Run is not the ACTIVE Run, so hydration reports no Run for it.
   * Without these a reloaded Retry has nothing authoritative to re-ask, and a
   * shared transcript's later messages make "the last user message" the wrong
   * request.
   */
  trigger_type?: Run["trigger_type"];
  trigger_message_id?: string | null;
  requested_by_user_id?: string | null;
  /**
   * Whether re-asking this Run's request can duplicate nothing.
   *
   * SERVER-AUTHORITATIVE and FAIL-CLOSED: absent means NOT CONFIRMED, which is
   * treated exactly like `false`. A client cannot compute this — it would have
   * to decide from the Proposals it happens to hold, which is a paged, lagging
   * subset — so the only honest answers are "the server said yes" and "not
   * confirmed".
   */
  retry_safe?: boolean;
}

export interface MainContextObservation {
  kind: "main";
  source: "provider";
  input_tokens: number;
  dispatch_id: string;
  generation: number;
  ordinal: number;
  anchor_eligible: boolean;
  observed_at: string;
  observation_sequence: number;
  prospective_estimate: number;
  checkpoint_revision: number;
  run_id: string | null;
  job_id: string | null;
  segment_id: string | null;
  turn_ordinal: number;
  profile_id: string | null;
  connection_id: string | null;
  execution_pin_id: string | null;
  model: string | null;
}

export interface ThreadTokenUsage {
  /** Decimal strings preserve BIGINT totals beyond JavaScript's safe integer range. */
  input_tokens: string;
  output_tokens: string;
  total_tokens: string;
  recorded_from: string | null;
  coverage: "unmeasured" | "complete_since_recorded_from" | "partial";
  main_context?: MainContextObservation | null;
}

export interface Conversation {
  token_usage?: ThreadTokenUsage | null;
  id: string;
  conversation_type: ConversationType;
  owner_user_id: string | null;
  workspace_type: WorkspaceType | null;
  workspace_id: string | null;
  /** Additive metadata present only on root-private daily conversations. */
  today_local_date?: string | null;
  today_time_zone?: string | null;
  title: string;
  orchestration_version: number;
  created_at: string;
  updated_at: string;
}

export type MessageFeedbackRating = "helpful" | "unhelpful";

export interface MessageFeedback {
  id: string;
  conversation_id: string;
  message_id: string;
  submitted_by_user_id: string;
  rating: MessageFeedbackRating;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpsertMessageFeedbackInput {
  rating: MessageFeedbackRating;
  comment?: string | null;
}

export interface Message {
  id: string;
  conversation_id: string;
  sequence: number;
  role: "user" | "assistant" | "system";
  author_user_id: string | null;
  /**
   * Trusted, bounded name snapshot taken from the validated identity claim when
   * the message was written. Never client-supplied and never rewritten on a
   * later rename. Null on historical and non-user messages — render a generic
   * participant label rather than inventing one.
   */
  author_display_name?: string | null;
  /** Validated structured mentions; only these ever triggered a Run. */
  mentions?: MessageMention[];
  /** Validated deterministic commands; visible slash text alone is inert. */
  commands?: MessageCommand[];
  run_id: string | null;
  content: {
    text?: string;
    /**
     * Which authorized files returned content for this answer. Absent when no
     * document tool contributed. Historical messages may instead carry a
     * `citations` array; it stays readable and is never rewritten.
     */
    grounding_sources?: GroundingSourceRef[];
    /** Files explicitly associated with this user turn; UI metadata only. */
    attachments?: MessageAttachmentRef[];
    /** On a user message: the Player AI message it replies to. */
    reply_to?: MessageReplyReference;
    /** On a Player AI response: the message(s) its Run was started by. */
    trigger?: MessageTriggerPreview;
    /** On a Player AI response: the Run was stopped; this is what it had shown. */
    stopped?: boolean;
  } & Record<string, unknown>;
  created_at: string;
  viewer_feedback?: MessageFeedback | null;
}

export interface MessageAttachmentRef {
  attachment_id: string;
  display_filename: string;
}

export interface ConversationAttachment {
  id: string;
  conversation_id: string;
  document_id: string | null;
  upload_session_id: string | null;
  source: "upload" | "my_files";
  status: AttachmentStatus;
  display_filename: string | null;
  declared_media_type: string | null;
  declared_size_bytes: number | null;
  detected_media_type: string | null;
  verified_size_bytes: number | null;
  failure_code: string | null;
  retrieval: DocumentRetrieval | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentRetrieval {
  latest_retrieval_version: number;
  active_retrieval_version: number | null;
  status: DocumentRetrievalStatus;
  failure_code: string | null;
  retryable: boolean;
  chunk_count: number | null;
  profile_version: string;
  updated_at: string;
}

export interface DocumentUploadGrant {
  method: "PUT";
  url: string;
  required_headers: Record<string, string>;
  expires_at: string;
  maximum_size_bytes: number;
}

export interface ConversationAttachmentEnvelope {
  attachment: ConversationAttachment;
}

export interface ConversationAttachmentUploadEnvelope extends ConversationAttachmentEnvelope {
  upload: DocumentUploadGrant;
  /**
   * True when the server removed unstorable characters from the file name
   * (Task 3). The upload continues under the cleaned name.
   */
  display_filename_adjusted?: boolean;
}

export interface ConversationAttachmentPage {
  attachments: ConversationAttachment[];
  next_cursor: string | null;
}

export interface MyFile {
  document_id: string;
  display_filename: string;
  detected_media_type: string | null;
  verified_size_bytes: number | null;
  created_at: string;
  updated_at: string;
}

export interface MyFilePage {
  items: MyFile[];
  next_cursor: string | null;
}

export interface DocumentDownloadSession {
  url: string;
  expires_at: string;
}

export interface AttachedDocumentDeletePreview {
  document_id: string;
  active_link_count: number;
  delete_version: number;
}

export interface Run {
  ai_profile?: AIRunProfile | null;
  id: string;
  conversation_id: string;
  status: RunStatus;
  outcome: RunOutcome | null;
  trigger_type:
    | "message"
    | "next"
    | "group_mention"
    | "group_scheduled"
    | "group_command"
    /** Opens a phase the workspace just entered (Task 4). */
    | "phase_entry";
  /** Additive: absent from servers that predate group collaboration. */
  conversation_mode?: CollaborationMode;
  /**
   * When someone pressed Stop. Set while the Run winds down ("Stopping…") and
   * kept once it ends with outcome `stopped`.
   */
  stop_requested_at?: string | null;
  /** Exclusive lower bound of a group Run's focused delta; 0 otherwise. */
  input_after_message_sequence?: number;
  input_through_message_sequence: number;
  /** Which requests a group Run is answering. Message IDs only, never names. */
  focus?: RunFocus | null;
  /**
   * The Message this Run was started to answer, when a message started it.
   * An ID only; the client resolves it against the Message projection it is
   * already authorized to read.
   */
  trigger_message_id?: string | null;
  /** Null only for a scheduled group Run, which no human requested. */
  requested_by_user_id: string | null;
  runtime_release: string;
  model_policy_id: ModelPolicyId;
  assistant_message_delivery: AssistantMessageDelivery;
  model_turn_count: number;
  tool_call_count: number;
  active_execution_seconds: number;
  /**
   * Retry generation of the Run; 0 until the first attempt is claimed. This is
   * the authoritative fence for attempt-scoped projections — a client must not
   * infer the current attempt from whichever activity rows it happens to hold.
   */
  attempt_epoch: number;
  orchestration_contract_version: number;
  orchestration_execution_id: string | null;
  document_wait?: RunDocumentWait | null;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
}

export interface RunFocus {
  mention_count: number;
  message_ids?: string[];
}

export interface RunDocumentWait {
  attachment_ids: string[];
  pending_attachment_ids: string[];
  decision: "wait" | "continue" | null;
}

export interface Proposal {
  id: string;
  conversation_id: string;
  run_id: string;
  tool_id: string;
  contract_version: number;
  title: string;
  summary: string;
  input: Record<string, unknown>;
  scope_ref: ScopeRef | EmptyScopeRef;
  target_ref: TargetRef | WorkspaceTargetRef | IntegrationExecutionTargetRef;
  precondition: Record<string, unknown>;
  preview: Record<string, unknown>;
  approval_mode: "manual" | "automatic";
  widget_schema_version: number | null;
  approval_batch_id: string | null;
  approval_batch_ordinal: number | null;
  supersedes_proposal_id: string | null;
  stale_reason: string | null;
  status: ProposalStatus;
  expires_at: string;
  decided_by_user_id: string | null;
  decided_at: string | null;
  result: Record<string, unknown>;
  error: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export type PlatformActionBatchLifecycleStatus =
  | "pending_confirmation"
  | "applying"
  | "applied"
  | "partially_applied"
  | "failed"
  | "invalidated";

export interface PlatformActionBatch {
  id: string;
  conversation_id: string;
  revision: number;
  status: PlatformActionBatchLifecycleStatus;
  frozen: FrozenActionBatch;
  result: PlatformActionBatchResult | null;
  remainder_batch_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpsertPlatformActionBatchInput {
  client_action_id: string;
  batch_id?: string;
  expected_revision?: number;
  actions: readonly PlatformAction[];
}

export interface PlatformActionBatchEnvelope {
  batch: PlatformActionBatch;
}

export interface CurrentPlatformActionBatchEnvelope {
  batch: PlatformActionBatch | null;
}

export interface ConversationHydration {
  conversation: Conversation;
  capabilities: Capabilities;
  active_run: Run | null;
  pending_proposals: Proposal[];
  assistant_snapshot: AssistantSnapshot | null;
  active_progress: RunProgress | null;
  /** Additive: absent from servers that predate the activity contract. */
  active_activities?: RunActivity[];
  /** Present only when the latest Run failed; older servers omit it. */
  latest_failure?: RunFailure | null;
  /** Present only while group mode is active for this conversation. */
  group_settings?: GroupSettings | null;
  /** Present only for the owner of a root-private pending confirmation batch. */
  pending_platform_action_batch?: PlatformActionBatch | null;
  event_cursor: number;
}

export interface ConversationPage {
  conversations: Conversation[];
  next_cursor: string | null;
}

export interface TodayConversationEnvelope {
  conversation: Conversation;
  local_date: string;
  time_zone: string;
  created: boolean;
}

export interface MessagePage {
  messages: Message[];
  next_before_sequence: number | null;
}

export interface ProposalPage {
  proposals: Proposal[];
  next_cursor: string | null;
}

export interface MessageEnvelope {
  message: Message;
  event_cursor: number;
  /**
   * The Run this send started, in the same transaction (d8). Null or absent
   * when it started none: a plain message, a queued or paused mention, a
   * capacity-deferred one, or a server that predates the field.
   */
  run?: Run | null;
  /**
   * `capacity` when bounded admission deferred the mentioned Run. The message
   * and its mention are saved; only the Run waits (d18).
   */
  run_deferred?: "capacity" | null;
}

export interface MessageFeedbackEnvelope {
  feedback: MessageFeedback;
}

export interface RunEnvelope {
  run: Run;
  message: Message | null;
  event_cursor: number;
}

export interface ProposalDecisionEnvelope {
  proposal: Proposal;
  decision_status: "accepted";
}

export interface ProposalBatchDecisionItem {
  proposalId: string;
  decision: "approve" | "reject";
  reason?: string;
  revision?: ProposalRevisionInput;
}

export interface ProposalBatchDecisionEnvelope {
  approval_batch_id: string;
  proposals: Proposal[];
  decision_status: "accepted";
}

/**
 * One duplicate-review replacement the decider kept, with their wording.
 *
 * `section_index` names a section of the ALREADY-PINNED proposal input, so a
 * selection can only ever narrow what the group saw — it cannot introduce an
 * idea, a workshop or a section the drafted proposal did not contain.
 */
export interface DuplicateReplacementChoice {
  section_index: number;
  text: string;
}

/** A decider's bounded refinement of an already-pinned command. */
export type ProposalDecisionSelection = {
  kind: "hoshin_workshop_duplicates";
  replacements: DuplicateReplacementChoice[];
};

/**
 * The bounded, provider-neutral form an integration proposal may be edited
 * through. Compiled by playbook-be from the governed schema — never a raw
 * remote schema, and never carrying a value the platform binds itself.
 */
export interface EditableProposalForm {
  version: 1;
  /** Identifies the exact descriptor. Sent back as `base_revision_hash`. */
  revisionHash: string;
  fields: Array<{
    path: string;
    label: string;
    control: "text" | "textarea" | "number" | "toggle" | "select" | "items";
    required: boolean;
    requiredUnless?: { field: string; equals: string };
    editable: boolean;
    constraints?: {
      min?: number;
      max?: number;
      maxLength?: number;
      nonBlank?: boolean;
    };
    options?: Array<{ value: string; label: string }>;
  }>;
  values: Record<string, unknown>;
}

/**
 * An approver's edited value set.
 *
 * The FULL editable set, not a patch: a patch is ambiguous about what it was
 * computed against, and the server would have to guess at an intent the user
 * already expressed. `base_revision_hash` names the descriptor that was
 * actually edited, so an approval prepared against a shape that has since
 * moved is refused rather than applied to inputs nobody reviewed.
 */
export interface ProposalRevisionInput {
  version: 1;
  base_revision_hash: string;
  values: Record<string, unknown>;
}

export interface UserSettings {
  tone?: "direct" | "empathetic" | null;
  default_profile_id?: string | null;
  default_profile_generation?: number | null;
  preference_version?: number;
  auto_approve_internal_tools: boolean;
  model_policy_id: ModelPolicyId;
  /**
   * Whether Player AI may read this user's mood history through its read-only
   * tool, in their private conversations. Off by default; optional so a client
   * ahead of the service still reads the older settings shape.
   */
  allow_mood_context?: boolean;
}

export interface SpecialistSettings {
  module: "solve";
  specialist_id: string;
  /** Stored tenant override; `null` means the deployment fallback applies. */
  model_policy_id: ModelPolicyId | null;
  min_new_user_messages: number | null;
  effective_model_policy_id: ModelPolicyId;
  effective_min_new_user_messages: number;
  settings_version: number;
  updated_at: string | null;
}

export interface ConversationSpecialistSettings {
  specialist_id: string;
  /** Stored conversation override; `null` means tenant/deployment fallback. */
  model_policy_id: ModelPolicyId | null;
  effective_model_policy_id: ModelPolicyId;
  profile_id: string | null;
  availability_generation: number | null;
  profile_version: number;
  effective_profile_id: string | null;
  effective_profile_name: string | null;
  profile_source: "conversation" | "app" | "platform" | "unconfigured";
  profile_fallback_reason: "selection_unavailable" | null;
  can_change: boolean;
}

export type UpdateSpecialistSettingsInput =
  | {
      model_policy_id: ModelPolicyId | null;
      min_new_user_messages?: number | null;
    }
  | {
      model_policy_id?: ModelPolicyId | null;
      min_new_user_messages: number | null;
    };

/**
 * Restrained separate status for a terminally failed background canvas
 * review. Never a chat message and never a second AI voice.
 */
export interface BackgroundCanvasStatus {
  state: "idle" | "analysing" | "updated" | "failed";
  stage?:
    | "idle"
    | "requested"
    | "queued"
    | "reviewing_canvas"
    | "reading_widget"
    | "preparing_widget_change"
    | "preparing_canvas_proposal"
    | "awaiting_approval"
    | "applying"
    | "awaiting_canvas_delivery"
    | "updated"
    | "no_change"
    | "needs_input"
    | "conflict"
    | "failed"
    | "rejected"
    | "expired";
  cycle_id: string | null;
  run_id?: string | null;
  proposal_id?: string | null;
  widget_labels?: string[];
  started_at?: string | null;
  updated_at?: string | null;
  completed_at: string | null;
  summary?: string | null;
  missing_input?: string | null;
  conflict?: string | null;
  failure_code?: string | null;
  total_updates?: number | null;
  applied_updates?: number | null;
}

export interface BackgroundReviewFailure {
  specialist_job_id: string;
  error_code: string | null;
  occurred_at: string | null;
}

/**
 * The thread's current conversation-context state, as a durable notice.
 *
 * Content-free by contract — counts and a safe failure code. The checkpoint
 * body is private conversation content and never reaches a client.
 *
 * `failed` does NOT mean the thread is broken: the previous valid checkpoint
 * is still in force and the conversation continues on it. The Retry only
 * shortens the wait, because the next model call re-attempts by itself.
 */
export interface ContextCompactionNotice {
  status: "compacted" | "failed";
  covered_message_count: number;
  failure_code: string | null;
  occurred_at: string | null;
}

/** What an explicit compaction attempt did. */
export interface ContextCompactionResult {
  status: "compacted" | "not_needed" | "nothing_eligible" | "failed";
  tokens_before: number;
  tokens_after: number;
  covered_message_count: number;
}

export interface ModelPolicySummary {
  id: ModelPolicyId;
  label: string;
  connection_key: ModelPolicyConnectionKey;
  reasoning_effort: ReasoningEffort;
  available: boolean;
}

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance?: string;
  code: string;
  request_id?: string;
  /**
   * VALIDATION_FAILED only: one entry per failed location. `limit` and
   * `received` are present only on length errors, and only from servers that
   * send them (PB-5380).
   */
  fields?: ReadonlyArray<{
    path: string;
    type: string;
    limit?: number;
    received?: number;
  }>;
}

export interface PlayerAIEvent {
  id: number | null;
  eventType: string;
  data: Record<string, unknown>;
}

/**
 * Why a browser left the live line for the long poll (d3). Sent on every poll
 * so the server can count them; the server refuses any other value (d29).
 */
export type EventPollReason = "no_hello" | "send_check" | "quiet";

/**
 * One reply of `GET /v2/conversations/{id}/events/poll` (d29). `data` is the
 * object the live line JSON-encodes, not a string, and `cursor` is where the
 * next poll starts.
 */
export interface EventPollReply {
  events: Array<{ id: number; event: string; data: Record<string, unknown> }>;
  cursor: number;
}

/**
 * Where a conversation's ACTION AUTHORITY stands — separate from what that
 * authority currently says (`capabilities`).
 *
 * PB-5296's D7 originally conflated the two: a revalidation in flight masked
 * every capability as false, so the composer disabled itself, keyboard focus
 * dropped to `<body>` and a file chosen while the picker was open was silently
 * discarded — all during a check that then CONFIRMED the access. Task 7 splits
 * them, and the split has to be explicit rather than derived from a boolean,
 * because "we are asking" and "the answer was no" call for opposite behaviour.
 *
 * - `checking` — a revalidation is in flight. The LAST CONFIRMED capabilities
 *   stay in force; the server gates every action, and a refusal is handled
 *   where it happens.
 * - `valid` — confirmed by the most recent successful read.
 * - `unavailable` — the check FAILED (transport, outage). Fail closed: block
 *   permission-dependent actions, keep the draft, offer a retry.
 * - `denied` — the server answered 403/404. Apply the returned restrictions.
 */
export type PlayerAIAuthorityStatus =
  | "valid"
  | "checking"
  | "unavailable"
  | "denied";

export interface UsePlayerAIResult {
  connectionState: ConnectionState;
  streamReconnectAttempt: number;
  /**
   * The human-readable sentence for {@link failure}, or `null`.
   *
   * Kept as a string for hosts that only render text, and it is now always
   * catalogue copy — never a raw code, a problem `detail`, an exception message
   * or a serialized object (PB-5276). Prefer {@link failure}, which carries the
   * same wording plus the recovery action and the safe diagnostics.
   */
  error: string | null;
  /**
   * The structured form of the current failure, or `null` when there is none.
   *
   * ONE model for every path a failure can arrive by — session setup, a request,
   * a terminal Run, a refused apply — so a host wires actions instead of
   * matching on strings, and the same failed Run presents identically live and
   * after a reload.
   */
  failure: PlayerAIFailure | null;
  /**
   * The refresh status above. A host renders `unavailable` and `denied` as a
   * read-only draft with a reason, and `checking` as nothing at all.
   */
  authorityStatus: PlayerAIAuthorityStatus;
  /**
   * Set when the failure in `error` stopped this scope from EVER opening a
   * conversation — a dependency outage during session setup, rather than a
   * failed Run or send (PB-1586).
   *
   * The same string as `error`, not a second message: it is a classification,
   * and its only job is to tell a host that `retryInitialize` can recover it in
   * place. Without the distinction the panel could only offer a page reload,
   * and its disabled composer read "This conversation is read-only." — a claim
   * about authorization that an outage had not made.
   */
  initializationError: string | null;
  /**
   * Re-run initialization for the current scope. Safe to call repeatedly: it
   * grants nothing, re-asks the server, and a recovered session is admitted on
   * exactly the capabilities the server answers with. A call made while a setup
   * is still in flight is IGNORED rather than queued — overlapping attempts can
   * create a duplicate workspace-private conversation — so a host may call it
   * without tracking loading state of its own.
   */
  retryInitialize: () => void;
  conversations: Conversation[];
  activeConversationId: string | null;
  messages: Message[];
  proposals: Proposal[];
  /**
   * The one pending root-private cross-app batch awaiting exact confirmation,
   * or `null`. Always `null` for shared and module-specific conversations,
   * which have no platform mutation surface.
   */
  platformActionBatch: PlatformActionBatch | null;
  run: Run | null;
  capabilities: Capabilities | null;
  /**
   * The surface the SERVER routed this scope to, or `null` when the scope does
   * not resolve one. Never inferred from the route: a host that guessed could
   * label a shared thread private.
   */
  conversationSurface: ConversationSurface | null;
  /**
   * Whether the SERVER considers this actor the workspace's effective driver.
   * Reported, never derived from a role — the drive moves with a facilitator
   * grant and the owner is only the fallback. `false` until answered.
   */
  isEffectiveDriver: boolean;
  eventCursor: number;
  assistantDraft: string;
  runProgress: RunProgress | null;
  /** Active-Run activity in deterministic order; empty once the Run ends. */
  runActivities: RunActivity[];
  /** Latest durable failed Run, used to keep its retry card across reopen. */
  runFailure: RunFailure | null;
  settings: UserSettings;
  modelPolicies: ModelPolicySummary[];
  /**
   * Durable SOLVE group collaboration facts, or `"individual"` (ADR-0044 AC1).
   * Never derived from presence, and never from how many people are typing.
   */
  collaborationMode: CollaborationMode;
  /** Present only while group mode is active for this conversation. */
  groupSettings: GroupSettings | null;
  /** Authority-only settings for the background SOLVE Canvas agent. */
  /** SOLVE's shared tone, or null outside a SOLVE workspace conversation. */
  solveResponseStyle: SolveResponseStyle | null;
  /** Whether this caller may change it; false leaves the control read-only. */
  canChangeSolveResponseStyle: boolean;
  setSolveResponseStyle: (style: SolveResponseStyle) => Promise<void>;
  /** Shared SOLVE-level web-search permission, or null outside SOLVE. */
  webSearchEnabled: boolean | null;
  canChangeWebSearch: boolean;
  setWebSearchEnabled: (enabled: boolean) => Promise<void>;
  solveWebSearchEnabled: boolean | null;
  canChangeSolveWebSearch: boolean;
  setSolveWebSearchEnabled: (enabled: boolean) => Promise<void>;
  backgroundCanvasSettings: ConversationSpecialistSettings | null;
  conversationProfile: ConversationProfileConfiguration | null;
  /**
   * Last terminal background canvas-review failure for this conversation, or
   * `null`. Cleared on conversation change; deliberately not persisted.
   */
  backgroundReviewFailure: BackgroundReviewFailure | null;
  /**
   * The thread's CURRENT context-compaction state, or `null`.
   *
   * Last-one-wins rather than a list: it describes where the conversation's
   * context stands now, not its history, so a thread that failed once and then
   * succeeded shows the success and its Retry button goes away. Cleared on
   * conversation change; deliberately not persisted.
   */
  contextNotice: ContextCompactionNotice | null;
  /**
   * "Try again" on a failed compaction. Safe to call at any time: the
   * transcript is never edited, a failure leaves the previous checkpoint in
   * force, and the publish is a compare-and-swap whose coverage only moves
   * forward — so racing the automatic pass loses cleanly.
   */
  retryContextCompaction: () => Promise<void>;
  canvasStatusLabel?: string | null;
  canvasStatus?: BackgroundCanvasStatus | null;
  canvasStatusUnavailable?: boolean;
  retryCanvasStatus?: () => void;
  isRunActive: boolean;
  isConversationLoading: boolean;
  isSubmittingMessage: boolean;
  hasMoreMessages: boolean;
  isLoadingMessages: boolean;
  createConversation(): Promise<boolean>;
  selectConversation(conversationId: string): Promise<boolean>;
  /**
   * Silently reconcile the active conversation with its workspace projection.
   * Keeps the live stream mounted and preserves the visible transcript while
   * product resource-change nudges become durable system messages.
   */
  refreshConversation(permissionsOnly?: boolean): Promise<void>;
  sendMessage(
    text: string,
    interactionContext?: InteractionContext,
    options?: {
      readonly startRun?: boolean;
      readonly attachmentIds?: readonly string[];
      /**
       * Carry a structured `player_ai` mention selected from autocomplete.
       * The server also recognizes the portable `[Player AI]` text token.
       */
      readonly mentionsPlayerAI?: boolean;
      readonly playerAIMentionOccurrenceIndices?: readonly number[];
      /**
       * People selected from the mention picker (Task 6). A message naming
       * only people addresses them and is not a Player AI request; nobody is
       * notified.
       */
      readonly personMentions?: readonly {
        readonly userId: string;
        readonly token: string;
        readonly occurrenceIndices: readonly number[];
      }[];
      /** The Player AI message this one replies to (Task 7). */
      readonly replyToMessageId?: string;
      readonly command?: "next_phase";
      /**
       * Called once the server has taken the message. The result is `false`
       * when the chat on screen changed while it was sent, although the
       * message was sent; a caller that must report the real outcome uses
       * this to tell the two apart.
       */
      readonly onAccepted?: () => void;
    },
  ): Promise<boolean>;
  decideDocumentWait(decision: "wait" | "continue"): Promise<void>;
  startNext(): Promise<void>;
  /**
   * Record a decision. Resolves `true` only when the server accepted it and the
   * same conversation is still active, so a caller that chains follow-up work on
   * a decision (Regenerate) can tell a real discard from a failed one.
   */
  decideProposal(
    proposalId: string,
    decision: "approve" | "reject",
    reason?: string,
    selection?: ProposalDecisionSelection,
    revision?: ProposalRevisionInput,
  ): Promise<boolean>;
  /** Decide every child of one parallel integration-write approval together. */
  decideProposalBatch(
    approvalBatchId: string,
    items: ProposalBatchDecisionItem[],
  ): Promise<boolean>;
  editPlatformActionBatch(actions: readonly PlatformAction[]): Promise<void>;
  applyPlatformActionBatch(): Promise<void>;
  loadPlatformActionBatch(batchId: string): Promise<void>;
  /** Facilitator-only (Owner fallback) group review schedule. */
  updateGroupSettings(input: {
    readonly schedule: GroupSchedule;
  }): Promise<void>;
  loadBackgroundCanvasSettings(): Promise<void>;
  setBackgroundCanvasModelPolicy(modelPolicyId: ModelPolicyId): Promise<void>;
  setConversationProfile(profileId: string | null): Promise<void>;
  setBackgroundCanvasProfile(profileId: string | null): Promise<void>;
  setAutoApproveInternalTools(enabled: boolean): Promise<void>;
  setModelPolicy(modelPolicyId: ModelPolicyId): Promise<void>;
  saveMessageFeedback(
    conversationId: string,
    messageId: string,
    input: UpsertMessageFeedbackInput,
  ): Promise<MessageFeedback>;
  loadOlderMessages(): Promise<void>;
  /**
   * Load older pages until the message with `sequence` is in `messages`, for
   * navigating to a mention or a referenced message outside the loaded
   * history. Resolves whether it is now loaded.
   */
  loadMessagesThrough(sequence: number): Promise<boolean>;
  /** The messages that mention the viewer, whole history; null on failure. */
  loadMentionIndex(): Promise<MentionIndex | null>;
  /** Stop the active Run (Task 14); the server checks who may. */
  stopRun(): Promise<void>;
  /** Facilitator or owner: one new Run for what waited while stopped. */
  resumePendingRequests(): Promise<void>;
  /** Facilitator or owner: clear what waited, keeping the messages. */
  discardPendingRequests(): Promise<void>;
  /** Everyone typing in this group conversation, the viewer included. */
  typingParticipants: TypingParticipant[];
  /** This tab's typing state; fire-and-forget, the caller throttles. */
  signalTyping(state: "typing" | "idle"): void;
}

export type AIAppId = "home" | "solve" | "hoshin" | "vsm";
/** A profile is addressed by its immutable id; there are no revisions. */
export interface AIProfileSummary {
  id: string;
  display_name: string;
  description: string | null;
  provider_name?: string | null;
  model_name?: string | null;
  availability_generation: number;
  /** AIS's verdict for the model; absent from an older Player AI means true. */
  web_search_supported?: boolean;
}
/**
 * The AIS run pin. `fallback_reason` is set only when the member's EXPLICIT
 * selection was unavailable; a changed default is followed silently.
 */
export interface AIRunProfile {
  pin_id: string;
  profile_id: string;
  connection_id: string;
  display_name: string;
  connection_display_name: string;
  app_id: AIAppId;
  source: "user" | "app" | "platform";
  fallback_reason: "selection_unavailable" | null;
  expires_at: string;
}
export interface AppProfileConfiguration {
  settings: {
    app_id: AIAppId;
    profile_id: string | null;
    availability_generation: number | null;
    version: number;
    effective_profile_id: string | null;
    effective_profile_name: string | null;
    default_profile_name?: string | null;
    default_source?: "app" | "platform" | "unconfigured";
    source: "user" | "app" | "platform" | "unconfigured";
    fallback_reason: "selection_unavailable" | null;
  };
  profiles: AIProfileSummary[];
}

export interface ConversationProfileConfiguration {
  settings: {
    app_id: AIAppId;
    profile_id: string | null;
    availability_generation: number | null;
    version: number;
    effective_profile_id: string | null;
    effective_profile_name: string | null;
    source: "conversation" | "app" | "platform" | "unconfigured";
    fallback_reason: "selection_unavailable" | null;
    can_change: boolean;
  };
  profiles: AIProfileSummary[];
}

export interface UserSettingsPatch {
  auto_approve_internal_tools?: boolean;
  model_policy_id?: ModelPolicyId;
  tone?: "direct" | "empathetic";
  default_profile_id?: string | null;
  /** Requires `expected_version`, like every other personal preference. */
  allow_mood_context?: boolean;
  expected_version?: number;
}
export interface PersonalProfileCatalogue {
  profiles: {
    id: string;
    display_name: string;
    description?: string | null;
    provider_name?: string | null;
    model_name?: string | null;
    availability_generation: number;
  }[];
  platform_profile_id: string | null;
  apps: Record<string, string | null>;
}
